import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Debug, urlParser } from '@hh.ru/browser-api-utils';

import { Params } from './types';
import urlHelper from './url';

type PushSource = 'Link' | 'usePush';

export interface PushKnownState {
    /** Дополнительные query-параметры */
    additionalQueryParams?: Record<string, unknown>;
    /** Отменяет корректирование позиции скролла при SPA-навигации */
    cancelScrollCorrection?: boolean;
    /** Признак ссылки, доступной роботам.
     * Таким ссылкам не добавляем hhtm-параметры, чтобы не делать дубли страниц.
     * Подробнее: https://webmaster.yandex.ru/blog/kak-nayti-dubli-stranits-s-neznachaschimi-get-parametrami
     */
    isSeoLink?: boolean;
    /** Включает подстановку hhtm-параметров в external (внешние) ссылки, с другим доменом */
    forceHhtmForExternalLink?: boolean;
    /** Включает подстановку hhtm-параметров в internal (внутренние) ссылки, с текущим доменом, при SPA переходах.
     * hhtm headers request не отправляются */
    forceHhtmForInternalLink?: boolean;
}

/** Дополнительный стейт `router.location.state`, который нужно передать при страничном переходе */
export type PushState = PushKnownState & Record<string, unknown>;

export type UseMakePushDataParams = PushState & Params;

interface PushData {
    useRoutingBySPA: boolean;
    useExternalLink: boolean;
    url: string;
    pushAction: CallHistoryMethodAction;
    // tempexp_31848_start
    hhtm: {
        from?: string;
        fromLabel?: string;
    };
    // tempexp_31848_end
}

type MakePushData = (to: string, state?: PushState, params?: Params) => PushData;

export default (source: PushSource): MakePushData => {
    /* У прокси компонентов SPA Middleware отключена */
    const isProxiedComponent = useSelector(({ isProxiedComponent }) => isProxiedComponent);
    const hhtmSource = useSelector(({ analyticsParams }) => analyticsParams?.hhtmSource);
    // Нужно смотреть именно на origin, потому что request.host
    // неправильно работает на hh.ru.STAND.pyn.ru (значение остаётся hh.ru).
    // А в request.origin приходит https://hh.ru.STAND.pyn.ru
    const origin = useSelector(({ request }) => request?.origin || null);

    return useCallback(
        (to: string, state?: PushState, params?: Params): PushData => {
            const {
                isSeoLink = false,
                additionalQueryParams,
                cancelScrollCorrection,
                forceHhtmForExternalLink,
                forceHhtmForInternalLink,
                ...additionalState
            } = state ?? {};
            const { cancelFetchingData = false } = params ?? {};

            const isAbsoluteLink = urlHelper.isAbsolute(to);
            const isInternalLink = urlHelper.isInternal(to, origin);
            const isSpecialLink = urlHelper.isSpecial(to);

            let url = to;
            let parsedUrl;
            let hhtmFrom;
            let hhtmFromLabel;

            try {
                parsedUrl = urlParser(to);
            } catch (_) {
                Debug.log('out error', new Error(`redux-spa-middleware ${source}: Invalid URL`), { to });
            }

            if (parsedUrl) {
                parsedUrl.params = Object.assign(parsedUrl.params, additionalQueryParams);

                const isInternalLinkWithHhtm = isInternalLink && !isSeoLink;
                const isExternalLinkWithHhtm = !isInternalLink && forceHhtmForExternalLink;

                if (isInternalLink || isExternalLinkWithHhtm) {
                    hhtmFrom = (parsedUrl.params.hhtmFrom as string | string[]) || hhtmSource || undefined;
                    hhtmFromLabel = (parsedUrl.params.hhtmFromLabel as string | string[]) || undefined;
                }

                if (isInternalLinkWithHhtm || isExternalLinkWithHhtm) {
                    parsedUrl.params.hhtmFrom =
                        (parsedUrl.params.hhtmFrom as string | string[]) || hhtmSource || undefined;
                    parsedUrl.params.hhtmFromLabel = hhtmFromLabel =
                        (parsedUrl.params.hhtmFromLabel as string | string[]) || undefined;
                } else {
                    delete parsedUrl.params.hhtmFrom;
                    delete parsedUrl.params.hhtmFromLabel;
                }
                url = parsedUrl.href;
            }

            const useRoutingBySPA = (isInternalLink || (isAbsoluteLink && !origin)) && !isProxiedComponent;
            const useExternalLink =
                !useRoutingBySPA && !isSeoLink && !isSpecialLink && urlHelper.isExternalLink(to, origin);

            return {
                useRoutingBySPA,
                useExternalLink,
                url,
                // tempexp_31848_start
                hhtm: {
                    from: Array.isArray(hhtmFrom) ? hhtmFrom[0] : hhtmFrom,
                    fromLabel: Array.isArray(hhtmFromLabel) ? hhtmFromLabel[0] : hhtmFromLabel,
                },
                // tempexp_31848_end
                pushAction: push(
                    to,
                    {
                        additionalQueryParams,
                        cancelScrollCorrection,
                        forceHhtmForExternalLink,
                        forceHhtmForInternalLink,
                        ...additionalState,
                    },
                    { cancelFetchingData }
                ),
            };
        },
        [isProxiedComponent, hhtmSource, source, origin]
    );
};
